import { useCallback, useEffect, useRef, useState } from 'react';
import { useInvoiceCelebrations } from '../../hooks/useInvoiceCelebrations';
import TeamCelebrationModal from './TeamCelebrationModal';

/**
 * Delivers the team invoice celebration on app load.
 *
 * Mounted once in AuthenticatedLayout rather than on the invoice page, because
 * this is a celebration for the whole team: it is delivered wherever a person
 * happens to land next, and the sender's own invoice is in their digest too.
 *
 * Fetched once per mount. A single dismissal acknowledges everything shown, so
 * a reload gets nothing until the team sends the next invoice.
 */
export default function TeamCelebration() {
  const { fetchCelebrations, acknowledgeCelebrations } = useInvoiceCelebrations();
  const [state, setState] = useState({ celebrations: [], moreCount: 0, seenThrough: null, cheer: 0 });
  const requested = useRef(false);

  useEffect(() => {
    // Guarded against React's development double-invoke, which would otherwise
    // fire two requests on every load.
    if (requested.current) return undefined;
    requested.current = true;

    let cancelled = false;
    (async () => {
      try {
        const result = await fetchCelebrations();
        if (cancelled || !result?.celebrations?.length) return;
        setState({
          celebrations: result.celebrations,
          moreCount: result.moreCount || 0,
          seenThrough: result.seenThrough,
          cheer: 1,
        });
      } catch {
        // A celebration is the lowest-stakes thing in the app. There is nothing
        // useful to tell the user on failure, and nothing to retry — the same
        // invoices are still pending on the next load.
      }
    })();

    return () => { cancelled = true; };
  }, [fetchCelebrations]);

  const dismiss = useCallback(() => {
    const ackThrough = state.seenThrough;
    setState({ celebrations: [], moreCount: 0, seenThrough: null, cheer: 0 });
    if (!ackThrough) return;
    // Fire-and-forget: the modal is already gone. A failed acknowledgement only
    // means the same celebration shows again next load, which is harmless.
    acknowledgeCelebrations(ackThrough).catch(() => {});
  }, [state.seenThrough, acknowledgeCelebrations]);

  return (
    <TeamCelebrationModal
      celebrations={state.celebrations}
      moreCount={state.moreCount}
      cheer={state.cheer}
      onDismiss={dismiss}
    />
  );
}
